import React, { useState, useEffect } from "react";
import { X, Monitor, Smartphone, Globe, Clock, Cpu, Activity, ChevronDown, ChevronUp, ExternalLink, Calendar, Eye, ShoppingCart, DollarSign, MessageSquare, MousePointer } from "lucide-react";

const UserActivityModal = ({ isOpen, onClose, userUid, userName }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSession, setExpandedSession] = useState(null);

  useEffect(() => {
    if (isOpen && userUid) {
      fetchSessions();
    }
  }, [isOpen, userUid]);

  useEffect(() => {
    if (!isOpen) {
      setSessions([]);
      setExpandedSession(null);
      setError(null);
    }
  }, [isOpen]);

  const formatTime = (seconds) => {
    if (!seconds || seconds === 0) return "0s";
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    const parts = [];
    if (hrs > 0) parts.push(`${hrs}h`);
    if (mins > 0) parts.push(`${mins}m`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);
    return parts.join(" ");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const fetchSessions = async () => {
    console.log("userUid",userUid)
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("adminAuth");
      const url = `${process.env.REACT_APP_BASEURL}/analytics/analytics-admin/user-sessions/?user=${userUid}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Token ${token}`,
        },
      });
 console.log("response", response)
      if (!response.ok) {
        throw new Error(`Failed to fetch sessions: ${response.status}`);
      }

      const data = await response.json();
      setSessions(data || []);
    } catch (err) {
      console.error("Error fetching user sessions:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case "buy_now":
        return <ShoppingCart size={14} className="text-emerald-500" />;
      case "Make_payment":
        return <DollarSign size={14} className="text-blue-500" />;
      case "ask_price":
        return <MessageSquare size={14} className="text-amber-500" />;
      case "popup_close":
        return <MousePointer size={14} className="text-purple-500" />;
      case "view_product":
        return <Eye size={14} className="text-cyan-500" />;
      default:
        return <Activity size={14} className="text-gray-500" />;
    }
  };

  const getEventBadge = (eventType) => {
    const colors = {
      buy_now: "bg-emerald-100 text-emerald-700 border-emerald-300",
      Make_payment: "bg-blue-100 text-blue-700 border-blue-300",
      ask_price: "bg-amber-100 text-amber-700 border-amber-300",
      popup_close: "bg-purple-100 text-purple-700 border-purple-300",
      view_product: "bg-cyan-100 text-cyan-700 border-cyan-300",
    };
    return colors[eventType] || "bg-gray-100 text-gray-700 border-gray-300";
  };

  const getDeviceIcon = (device) => {
    if (device?.toLowerCase() === "desktop") return <Monitor size={16} />;
    if (device?.toLowerCase() === "mobile") return <Smartphone size={16} />;
    return <Globe size={16} />;
  };

  if (!isOpen) return null;

  const totalSessions = sessions.length;
  const totalEvents = sessions.reduce((sum, s) => sum + (s.total_events || 0), 0);
  const totalTimeSpent = sessions.reduce((sum, s) => sum + (s.time_spent_seconds || 0), 0);

  return (
    <div className="fixed inset-0 z-[99999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-[80vw] max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl">
              <Activity size={22} />
            </div>
            <div>
              <h2 className="text-xl font-bold !text-white">User Activity</h2>
              <p className="text-sm text-white/80">
                {userName || `User #${userUid?.slice(0, 8)}`} 
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-white/20 transition"
          >
            <X size={22} />
          </button>
        </div>

        {/* Summary Stats */}
        {!loading && !error && sessions.length > 0 && (
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl shadow-sm border border-gray-100">
                <Activity size={16} className="text-teal-600" />
                <span className="text-sm text-gray-600">
                  <strong className="text-gray-800">{totalSessions}</strong> Sessions
                </span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl shadow-sm border border-gray-100">
                <Cpu size={16} className="text-teal-600" />
                <span className="text-sm text-gray-600">
                  <strong className="text-gray-800">{totalEvents}</strong> Events
                </span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl shadow-sm border border-gray-100">
                <Clock size={16} className="text-teal-600" />
                <span className="text-sm text-gray-600">
                  <strong className="text-gray-800">{formatTime(totalTimeSpent)}</strong> Total
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="overflow-y-auto max-h-[calc(90vh-180px)] p-6 bg-gray-50/50">
          {loading ? (
            <div className="h-full min-h-[400px] flex items-center justify-center">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                <p className="mt-4 text-gray-500 font-medium">
                  Loading sessions...
                </p>
              </div>
            </div>
          ) : error ? (
            <div className="h-full min-h-[400px] flex items-center justify-center">
              <div className="text-center max-w-md">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                  <X size={28} className="text-red-500" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-gray-700">
                  Failed to Load Sessions
                </h3>
                <p className="text-sm text-gray-500 mt-1">{error}</p>
                <button
                  onClick={fetchSessions}
                  className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition text-sm font-medium"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : sessions.length === 0 ? (
            <div className="h-full min-h-[400px] flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                  <Activity size={28} className="text-gray-400" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-gray-600">
                  No Sessions Found
                </h3>
                <p className="text-gray-400 text-sm mt-1">
                  This user has no recorded sessions yet.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {sessions.map((session, sIndex) => {
                const isExpanded = expandedSession === session.session_id;
                const shortId = session.session_id?.slice(0, 8);

                return (
                  <div
                    key={session.session_id}
                    className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden transition-all hover:shadow-md"
                  >
                    {/* Session Header - Clickable */}
                    <button
                      onClick={() =>
                        setExpandedSession(
                          isExpanded ? null : session.session_id
                        )
                      }
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition text-left"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        {/* Session Number Badge */}
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white font-bold text-sm shadow-sm flex-shrink-0">
                          S{sIndex + 1}
                        </div>

                        {/* Session Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-gray-800">
                              Session {shortId}
                            </span>
                            <span className="text-[11px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-mono">
                              {session.ip_address || "—"}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
                            <span className="flex items-center gap-1">
                              {getDeviceIcon(session.device)}
                              {session.device || "—"} · {session.os || "—"} ·{" "}
                              {session.browser || "—"}
                            </span>
                            <span className="flex items-center gap-1">
                              <Cpu size={12} />
                              {session.total_events || 0} events
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={12} />
                              {formatTime(session.time_spent_seconds)}
                            </span>
                          </div>
                        </div>

                        {/* Dates */}
                        <div className="hidden md:block text-right text-xs text-gray-400">
                          <div className="flex items-center gap-1">
                            <Calendar size={11} />
                            {formatDate(session.first_visit)}
                          </div>
                          <div className="mt-0.5">→ {formatDate(session.last_visit)}</div>
                        </div>
                      </div>

                      {/* Expand Icon */}
                      <div className="ml-3 p-1.5 rounded-lg hover:bg-gray-200 transition">
                        {isExpanded ? (
                          <ChevronUp size={18} className="text-teal-600" />
                        ) : (
                          <ChevronDown size={18} className="text-gray-400" />
                        )}
                      </div>
                    </button>

                    {/* Expanded Activities */}
                    {isExpanded && (
                      <div className="border-t border-gray-100 bg-gray-50/50">
                        {session.activities?.length > 0 ? (
                          <div className="divide-y divide-gray-100">
                            {session.activities.map((activity, aIndex) => (
                              <div
                                key={aIndex}
                                className="flex items-start gap-3 p-4 hover:bg-white transition"
                              >
                                {/* Timeline dot */}
                                <div className="flex flex-col items-center pt-1">
                                  <div className="w-2 h-2 rounded-full bg-teal-400"></div>
                                  {aIndex < session.activities.length - 1 && (
                                    <div className="w-0.5 h-full bg-gray-200 mt-1"></div>
                                  )}
                                </div>

                                {/* Activity Content */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <span
                                      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${getEventBadge(
                                        activity.event_type
                                      )}`}
                                    >
                                      {getEventIcon(activity.event_type)}
                                      {activity.event_type?.replace(/_/g, " ")}
                                    </span>
                                    {activity.button_name && (
                                      <span className="text-[11px] text-gray-400">
                                        via "{activity.button_name}"
                                      </span>
                                    )}
                                    <span className="text-[11px] text-gray-400 ml-auto">
                                      {formatDate(activity.timestamp)}
                                    </span>
                                  </div>

                                  {activity.product && (
                                    <p className="text-sm font-medium text-gray-700 truncate">
                                      {activity.product}
                                    </p>
                                  )}

                                  {activity.page && (
                                    <a
                                      href={`${process.env.REACT_APP_FRONTEND_URL || ""}${activity.page}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 hover:underline mt-0.5"
                                    >
                                      <ExternalLink size={11} />
                                      {activity.page.length > 50
                                        ? activity.page.slice(0, 50) + "..."
                                        : activity.page}
                                    </a>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="py-8 text-center text-sm text-gray-400">
                            No activities recorded for this session.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserActivityModal;
